import DepartmentsPage from '@/departments/DepartmentsPage';
import { FiscalYearsPage } from '@/fiscalYears/FiscalYearsPage';
import { OrgAutoSelector } from '@/organization/OrgAutoSelector';
import { OrgProvider } from '@/organization/OrgContext';
import { ProfilePage } from '@/users/ProfilePage';
import { createHashRouter, Navigate, Outlet } from 'react-router-dom';
import AuthGuard from './auth/AuthGuard';
import { AuthProvider } from './auth/AuthProvider';
import LoginPage from './auth/Login';
import PasswordResetConfirm from './auth/PasswordResetConfirm';
import PasswordResetRequest from './auth/PasswordResetRequest';
import ResetPasswordPage from './auth/ResetPassword';
import SignupPage from './auth/Signup';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/Layout';
import { NotFound } from './components/NotFound';
import { CrumbProvider } from './crumbs/CrumbContext';
import { BuildingPurpose } from './decarbonization/building-purpose/BuildingPurpose';
import { CookingSelection } from './decarbonization/cooking/CookingSelection';
import { DecarbonizationHub } from './decarbonization/DecarbonizationHub';
import { DecarbOverview } from './decarbonization/DecarbOverview';
import { EnergySelection } from './decarbonization/energy-usage/EnergySelection';
import { HVACSelection } from './decarbonization/HVAC/HVACSelection';
import { LightingSelection } from './decarbonization/lighting/LightingSelection';
import { MonitoringSelection } from './decarbonization/monitoring/MonitoringSelection';
import { PlugLoadSelection } from './decarbonization/plugload/PlugLoadSelection';
import { SpaceUsage } from './decarbonization/space-usage/SpaceUsage';
import { SpecializedSelection } from './decarbonization/specialized/SpecializedSelection';
import { WaterHeaterSelection } from './decarbonization/water-heater/WaterHeaterSelection';
import { WaterPumpSelection } from './decarbonization/water-pumps/WaterPumpSelection';
import { LandingPage } from './organization/LandingPage';
import { OrganizationManagementPage } from './organization/OrganizationManagementPage';
import { OrganizationUsersPage } from './organization/OrganizationUsersPage';
import OrgGuard from './organization/OrgGuard';
import SelectOrganizationPage from './organization/SelectOrganizationPage';
import { HVAC } from './testing/HVAC';
import { Weather } from './testing/weather/Weather';
import { CostEstimates } from './tools/cost-estimates/CostEstimates';
import { ProjectDetails } from './tools/ProjDetails';
import { ProjectManagers } from './tools/ProjectDetails/ProjectManagers';
import { ProjectOverview } from './tools/ProjectDetails/ProjOverview';
import { SiteSelectionPage } from './tools/site-selection/SiteSelectionPage';
import { SubprojectDetails } from './tools/SubprojectDetails';
import { UsersManagementPage } from './users/UsersManagementPage';
/**
 * High level routes:
 * /
  ├── tools
  ├── testing
  │   ├── hvac
  │   ├── weather
  ├── project/:projectId
  │   ├── decarb/:subId
  │   │   ├── building-purpose
  │   │   ├── spaceusage
  │   │   ├── hvac-select
  │   │   ├── energy-select
  │   ├── decarb/overview
 */

export const Routes = {
  Root: {
    path: '/',
    Tools: {
      path: 'tools',
    },
    Departments: {
      path: 'departments',
    },
    FiscalYears: {
      path: 'fiscal-years',
    },
    Users: {
      path: 'users',
    },
    Profile: {
      path: 'profile',
    },
    Landing: {
      path: 'landing',
    },
    Testing: {
      path: 'testing',
      HVAC: {
        path: 'hvac',
      },
      Weather: {
        path: 'weather',
      },
    },
    Admin: {
      path: 'admin',
      Organizations: {
        path: 'organizations',
      },
      Users: {
        path: 'users',
      },
    },
  },
  ProjectOverview: {
    path: 'proj-overview',
  },
  Project: {
    path: 'project/:projectId',
    SiteSelection: {
      path: 'site-selection',
    },
    Decarbonization: {
      path: 'decarb/:subId',
      BuildingPurpose: {
        path: 'building-purpose',
      },
      SpaceUsage: {
        path: 'spaceusage',
      },
      LightingSelection: {
        path: 'lighting-select',
      },
      HVACSelection: {
        path: 'hvac-select',
      },
      WaterPumpSelection: {
        path: 'waterpump-select',
      },
      WaterHeaterSelection: {
        path: 'waterheater-select',
      },
      CookingSelection: {
        path: 'cooking-select',
      },
      MonitorSelection: {
        path: 'monitor-select',
      },
      PlugLoadSelection: {
        path: 'plugload-select',
      },
      SpecializedSelection: {
        path: 'specialized-select',
      },
      EnergySelection: {
        path: 'energy-select',
      },
    },
    DecarbonizationOverview: {
      path: 'decarb/overview',
    },
    ProjectDetails: {
      path: 'proj-details',
    },
    ProjectOverview: {
      path: 'proj-overview',
    },
    ProjectManagerial: {
      path: 'managerial',
    },
    CostEstimates: {
      path: 'cost-estimates',
    },
    SubprojectCreate: {
      path: 'subproj-create',
    },
    SubprojectDetails: {
      path: 'subproj/:subId',
    },
  },
} as const;

export const CSIRouter = createHashRouter(
  [
    {
      // Provide Auth context for all routes
      path: '/',
      element: (
        <OrgProvider>
          <AuthProvider>
            <CrumbProvider>
              <ErrorBoundary>
                <Outlet />
              </ErrorBoundary>
            </CrumbProvider>
          </AuthProvider>
        </OrgProvider>
      ),
      children: [
        // Public routes (no layout/navigation)
        {
          path: 'login',
          element: <LoginPage />,
        },
        {
          path: 'signup/:code',
          element: <SignupPage />,
        },
        {
          path: 'forgot',
          element: <PasswordResetRequest />,
        },
        {
          path: 'reset/:code',
          element: <PasswordResetConfirm />,
        },
        {
          path: 'reset-password',
          element: <ResetPasswordPage />,
        },
        // Protected routes (with layout/navigation)
        {
          element: <AuthGuard />,
          children: [
            { path: 'select-org', element: <SelectOrganizationPage /> },
            {
              path: '',
              element: (
                <OrgAutoSelector>
                  <Layout>
                    <OrgGuard />
                  </Layout>
                </OrgAutoSelector>
              ),
              children: [
                // Default index route: redirect or render a default component.
                // For example, if you want to mimic the old behavior, you might choose to redirect to "tools".
                {
                  index: true,
                  element: <Navigate to={Routes.Root.Landing.path} />,
                  handle: { crumb: { name: 'Home' } },
                },
                {
                  path: `${Routes.Root.Tools.path}/:fiscalYearId`, // "tools"
                  element: <LandingPage />,
                  handle: { crumb: { name: 'Tools' } },
                },
                {
                  path: Routes.Root.Departments.path,
                  element: <DepartmentsPage />,
                  handle: { crumb: { name: 'Departments' } },
                },
                {
                  path: Routes.Root.FiscalYears.path,
                  element: <FiscalYearsPage />,
                  handle: { crumb: { name: 'Fiscal Years' } },
                },
                {
                  path: Routes.Root.Users.path,
                  element: <UsersManagementPage />,
                  handle: { crumb: { name: 'Users' } },
                },
                {
                  path: Routes.Root.Profile.path,
                  element: <ProfilePage />,
                  handle: { crumb: { name: 'Profile' } },
                },
                {
                  path: Routes.Root.Landing.path,
                  element: <LandingPage />,
                  handle: { crumb: { name: 'Landing' } },
                },
                {
                  path: Routes.Root.Admin.path,
                  element: <Outlet />,
                  children: [
                    {
                      index: true,
                      element: (
                        <Navigate to={Routes.Root.Admin.Organizations.path} />
                      ),
                    },
                    {
                      path: Routes.Root.Admin.Organizations.path,
                      element: <OrganizationManagementPage />,
                      handle: { crumb: { name: 'Organizations' } },
                    },
                    {
                      path: Routes.Root.Admin.Users.path,
                      element: <OrganizationUsersPage />,
                      handle: { crumb: { name: 'Users' } },
                    },
                  ],
                },
                {
                  path: Routes.Root.Testing.path, // "testing"
                  handle: { crumb: { name: 'Testing' } },
                  children: [
                    {
                      path: Routes.Root.Testing.HVAC.path,
                      element: <HVAC />,
                      handle: { crumb: { name: 'HVAC' } },
                    },
                    {
                      path: Routes.Root.Testing.Weather.path,
                      element: <Weather />,
                      handle: { crumb: { name: 'Weather Forecast' } },
                    },
                  ],
                },
                {
                  path: Routes.ProjectOverview.path,
                  element: <ProjectOverview />,
                },
                {
                  path: Routes.Project.path,
                  element: <Outlet />,
                  children: [
                    {
                      path: Routes.Project.SiteSelection.path,
                      element: <SiteSelectionPage />,
                      handle: { crumb: { name: 'Site Selection' } },
                    },
                    {
                      path: Routes.Project.DecarbonizationOverview.path,
                      element: <DecarbOverview />,
                    },
                    {
                      path: Routes.Project.ProjectDetails.path,
                      element: <ProjectDetails />,
                    },
                    {
                      path: Routes.Project.CostEstimates.path,
                      element: <CostEstimates />,
                      handle: { crumb: { name: 'Cost Estimates' } },
                    },
                    {
                      path: Routes.Project.ProjectOverview.path,
                      element: <ProjectOverview />,
                    },
                    {
                      path: Routes.Project.ProjectManagerial.path,
                      element: <ProjectManagers></ProjectManagers>,
                    },
                    {
                      path: Routes.Project.SubprojectDetails.path,
                      element: <SubprojectDetails />,
                    },
                    {
                      path: Routes.Project.SubprojectCreate.path,
                      element: <SubprojectDetails />,
                    },
                    {
                      path: Routes.Project.Decarbonization.path,
                      element: <Outlet />,
                      children: [
                        {
                          // Default for decarb is DecarbonizationHub.
                          index: true,
                          element: <DecarbonizationHub />,
                        },
                        {
                          // Nested flow outlet for decarb details.
                          element: <Outlet />,
                          children: [
                            {
                              path: Routes.Project.Decarbonization
                                .BuildingPurpose.path,
                              element: <BuildingPurpose />,
                              handle: { crumb: { name: 'Building Purpose' } },
                            },
                            {
                              path: Routes.Project.Decarbonization.SpaceUsage
                                .path,
                              element: <SpaceUsage />,
                              handle: { crumb: { name: 'Space Usage' } },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .LightingSelection.path,
                              element: <LightingSelection />,
                              handle: { crumb: { name: 'Lighting Selection' } },
                            },
                            {
                              path: Routes.Project.Decarbonization.HVACSelection
                                .path,
                              element: <HVACSelection />,
                              handle: { crumb: { name: 'HVAC Selection' } },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .WaterPumpSelection.path,
                              element: <WaterPumpSelection />,
                              handle: {
                                crumb: { name: 'Waterpump Selection' },
                              },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .WaterHeaterSelection.path,
                              element: <WaterHeaterSelection />,
                              handle: {
                                crumb: { name: 'Waterheater Selection' },
                              },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .CookingSelection.path,
                              element: <CookingSelection />,
                              handle: { crumb: { name: 'Cooking Selection' } },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .MonitorSelection.path,
                              element: <MonitoringSelection />,
                              handle: { crumb: { name: 'Monitor Selection' } },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .PlugLoadSelection.path,
                              element: <PlugLoadSelection />,
                              handle: {
                                crumb: { name: 'Plug Load Selection' },
                              },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .SpecializedSelection.path,
                              element: <SpecializedSelection />,
                              handle: {
                                crumb: { name: 'Specialized Equipment' },
                              },
                            },
                            {
                              path: Routes.Project.Decarbonization
                                .EnergySelection.path,
                              element: <EnergySelection />,
                              handle: { crumb: { name: 'Energy Selection' } },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                {
                  // Catch all route / error boundary
                  path: '*',
                  element: <NotFound />,
                  errorElement: <ErrorBoundary />,
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
);
